import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { ToggleStage } from '@/components/Stage/ToggleStage';
import type { HeySynclyChat, HeySynclyChatMode } from '@/entities/hey-syncly';
import { StudioItemDetailContainer } from '@/features/Studio/components/StudioItemDetail/StudioItemDetailContainer';
import { cn } from '@/lib/cn';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { timeOut } from '@/utils/util';

import { HeySynclyChatLayout } from '../layout/hey-syncly-chat-layout';
import { HeySynclyPageProvider } from '../model/hey-syncly-page-store';
import ChatPanel from '../ui/chat-panel/chat-panel';

const RESIZE_HANDLE_WIDTH = 16;

export const ChatPage = () => {
  const chatMap = useHeySynclyStore((state) => state.chatMap);
  const newChat = useHeySynclyStore((state) => state.newChat);
  const selectChat = useHeySynclyStore((state) => state.selectChat);
  const tempAnswerChatAlias = useHeySynclyStore(
    (state) => state.tempAnswer?.chat_alias ?? null,
  );
  const pageRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const studioItemDetail = useHeySynclyStore((state) => state.studioItemDetail);
  const clearStudioItemDetail = useHeySynclyStore(
    (state) => state.clearStudioItemDetail,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();
  const chatAlias =
    (searchParams.get('chat_id') as Alias | null | undefined) ?? null;
  const chatMode: HeySynclyChatMode =
    searchParams.get('mode')?.toLowerCase() === 'report' ? 'REPORT' : 'GENERAL';

  const chat: HeySynclyChat | null = useMemo(() => {
    if (!chatAlias) return null;
    return chatMap.get(chatAlias) || null;
  }, [chatAlias, chatMap]);

  const [ratio, setRatio] = useState(1);
  const [onDrag, setOnDrag] = useState(false);
  const [contentShow, setContentShow] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const animationFrameRef = useRef<number>();

  // 컨텐츠 선택/닫힘 시 자동으로 비율 조정
  useEffect(() => {
    if (studioItemDetail) {
      // 컨텐츠가 선택되면 5:5 비율로 설정
      setContentShow(true);
      setRatio(0.5);
    } else {
      // 컨텐츠가 닫히면 전체 너비로 설정
      setContentShow(false);
      setRatio(1);
    }
  }, [studioItemDetail]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setOnDrag(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!onDrag || !pageRef.current) return;

      // requestAnimationFrame으로 성능 최적화
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        if (!pageRef.current) return;

        const { left, width } = pageRef.current.getBoundingClientRect();
        const panelAreaWidth = Math.max(width - RESIZE_HANDLE_WIDTH, 1);
        const relativeX = e.clientX - left;
        const newRatio = relativeX / panelAreaWidth;

        setRatio(Math.max(Math.min(newRatio, 0.7), 0.3));
      });
    },
    [onDrag],
  );

  const handleMouseUp = useCallback(() => {
    if (!onDrag) return;

    setOnDrag(false);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, [onDrag]);

  const handleClear = useCallback(async () => {
    setRatio(1);
    setContentShow(false);
    await timeOut(200);
    clearStudioItemDetail();
  }, [clearStudioItemDetail]);

  // 전역 이벤트 리스너 등록/해제
  useEffect(() => {
    const node = pageRef.current;

    if (!node) {
      return;
    }

    const updateContainerWidth = () => {
      setContainerWidth(node.clientWidth);
    };

    updateContainerWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateContainerWidth);

      return () => {
        window.removeEventListener('resize', updateContainerWidth);
      };
    }

    const observer = new ResizeObserver(updateContainerWidth);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (onDrag) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [onDrag, handleMouseMove, handleMouseUp]);

  const updateChatAliasInUrl = useCallback(
    (nextChatAlias: Alias | null, options?: { replace?: boolean }) => {
      const nextSearchParams = new URLSearchParams(searchParamsString);

      if (nextChatAlias) {
        nextSearchParams.set('chat_id', nextChatAlias);
      } else {
        nextSearchParams.delete('chat_id');
      }

      setSearchParams(nextSearchParams, options);
    },
    [searchParamsString, setSearchParams],
  );

  const updateChatModeInUrl = useCallback(
    (nextChatMode: HeySynclyChatMode, options?: { replace?: boolean }) => {
      const nextSearchParams = new URLSearchParams(searchParamsString);

      if (nextChatMode === 'REPORT') {
        nextSearchParams.set('mode', 'report');
      } else {
        nextSearchParams.delete('mode');
      }

      setSearchParams(nextSearchParams, options);
    },
    [searchParamsString, setSearchParams],
  );

  useEffect(() => {
    if (!chatAlias) {
      clearStudioItemDetail();
      return;
    }

    void selectChat({ to: chatAlias }).catch(() => {
      updateChatAliasInUrl(null, { replace: true });
    });
  }, [chatAlias, clearStudioItemDetail, selectChat, updateChatAliasInUrl]);

  const onChatSelect = useCallback(
    (to: Alias) => {
      updateChatAliasInUrl(to);
    },
    [updateChatAliasInUrl],
  );

  const onNewChat = useCallback(() => {
    clearStudioItemDetail();
    newChat();
    updateChatAliasInUrl(null);
  }, [clearStudioItemDetail, newChat, updateChatAliasInUrl]);

  useEffect(() => {
    if (!chat?.alias) {
      return;
    }

    if (chatAlias === chat.alias) {
      return;
    }

    updateChatAliasInUrl(chat.alias);
  }, [chat?.alias, chatAlias, updateChatAliasInUrl]);

  useEffect(() => {
    if (!tempAnswerChatAlias || chatAlias === tempAnswerChatAlias) {
      return;
    }

    updateChatAliasInUrl(tempAnswerChatAlias);
  }, [chatAlias, tempAnswerChatAlias, updateChatAliasInUrl]);

  const availablePanelWidth = Math.max(
    containerWidth - (studioItemDetail ? RESIZE_HANDLE_WIDTH : 0),
    0,
  );

  return (
    <HeySynclyPageProvider
      currentChatAlias={chatAlias}
      onChatSelect={onChatSelect}
      onNewChat={onNewChat}
    >
      <div ref={pageRef} className="flex h-screen w-full overflow-hidden">
        <div
          className="relative flex min-w-0 flex-col h-full overflow-hidden"
          style={{
            width: studioItemDetail
              ? `${availablePanelWidth * ratio}px`
              : '100%',
            transition: onDrag ? 'none' : 'width 0.3s ease-in-out',
          }}
        >
          <HeySynclyChatLayout>
            <ChatPanel
              chat={chat}
              chatMode={chatMode}
              onChatModeChange={updateChatModeInUrl}
            />
          </HeySynclyChatLayout>
        </div>

        {studioItemDetail && (
          <>
            <ResizeHandle ref={resizeHandleRef} onMouseDown={handleMouseDown} />
            <div
              className={cn(
                'relative z-10 min-w-0 animate-in slide-in-from-right',
              )}
              style={{
                width: `${availablePanelWidth * (1 - ratio)}px`,
                transition: onDrag ? 'none' : 'width 0.3s ease-in-out',
              }}
            >
              <ToggleStage
                status={contentShow}
                options={{
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  exit: { opacity: 0 },
                  transition: { duration: 0.1 },
                }}
              >
                <StudioItemDetailContainer
                  alias={studioItemDetail}
                  onClose={handleClear}
                />
              </ToggleStage>
            </div>
          </>
        )}
      </div>
    </HeySynclyPageProvider>
  );
};

const ResizeHandle = forwardRef(
  (
    props: ComponentPropsWithoutRef<'div'>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <div
        ref={ref}
        {...props}
        className="relative flex shrink-0 items-center justify-center w-4 h-full cursor-ew-resize"
      >
        <div className="relative w-px h-full bg-border">
          <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-12 rounded-full top-1/2 -translate-y-1/2 z-10 bg-muted shadow-[inset_0_0_2px_0_rgba(92,90,84,0.4)]" />
        </div>
      </div>
    );
  },
);
